import * as React from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { Paper, IconButton, Box, Typography, Chip, Select, MenuItem, FormControl, Tooltip, Menu, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@material-ui/core';
import SendIcon from '@material-ui/icons/Send';
import EmailIcon from '@material-ui/icons/Email';
import SmsIcon from '@material-ui/icons/Sms';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import PhoneIcon from '@material-ui/icons/Phone';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import ReplyIcon from '@material-ui/icons/Reply';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import CheckIcon from '@material-ui/icons/Check';
import DraftsIcon from '@material-ui/icons/Drafts';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import RefreshIcon from '@material-ui/icons/Refresh';
import PauseCircleOutlineIcon from '@material-ui/icons/PauseCircleOutline';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import GoogleIcon from './Icons/GoogleIcon';
import { CommunicationData, CommunicationType, CommunicationDirection, CommunicationStatus, GoogleReviewState, GoogleReviewSummary } from '../models/api-models';
import moment from 'moment';
import { getLanguage, useTranslations } from '../store/translations/hooks';
import { TK } from '../store/translations/translationKeys';
import { useDispatch, useSelector } from 'react-redux';
import { getMyProffesionalData, getMyUser } from '../store/session/selectors';
import { getGeneralSettings } from '../store/settings/selectors';
import { getCommunicationQuotas } from '../store/general/selectors';
import { loadCommunicationQuotasIfNeeded } from '../store/general/actions';

type TranslateFn = (key: TK, ...args: string[]) => string;

interface CommunicationChatProps {
  messages: CommunicationData[];
  sendMessage: (message: string, type: CommunicationType, subject?: string, attachments?: File[], conversationId?: string, observations?: string) => Promise<boolean> | boolean;
  onToggleRead?: (message: CommunicationData, isRead: boolean) => Promise<void> | void;
  onUpdateObservations?: (message: CommunicationData, observations?: string) => Promise<boolean> | boolean;
  disabled?: boolean;
  isOpen?: boolean;
  onRefresh?: () => void | Promise<void>;
  clientName?: string;
  clientPhoneNumbers?: string[];
  mintuPausedUntil?: string;
  onMintuPauseChange?: (pauseHours?: number) => Promise<void> | void;
  onRequestGoogleReview?: (channel: CommunicationType, branchId?: string) => Promise<void> | void;
  googleReviewSummary?: GoogleReviewSummary;
}

const ChatContainer = styled(Paper)`
  display: flex;
  flex-direction: column;
  height: 600px;
  max-height: 80vh;
`;

const MessagesContainer = styled(Box)`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background-color: #f5f5f5;
`;

const MessageBubble = styled(Box)<{ $isSent: boolean }>`
  display: flex;
  flex-direction: column;
  align-self: ${props => props.$isSent ? 'flex-end' : 'flex-start'};
  min-width: 45%;
  max-width: 70%;
  background-color: ${props => props.$isSent ? '#1976d2' : '#ffffff'};
  color: ${props => props.$isSent ? '#ffffff' : '#000000'};
  border-radius: 12px;
  padding: 12px;
  padding-bottom: 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  position: relative;
`;


const QuotedMessage = styled(Box)<{ $isSent: boolean }>`
  background-color: ${props => props.$isSent ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.05)'};
  border-left: 3px solid ${props => props.$isSent ? 'rgba(255, 255, 255, 0.5)' : '#1976d2'};
  padding: 8px;
  margin-bottom: 8px;
  border-radius: 4px;
  font-size: 0.85rem;
  cursor: pointer;

  &:hover {
    background-color: ${props => props.$isSent ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.08)'};
  }
`;

const QuotedMessageText = styled(Typography)`
  font-size: 0.8rem;
  opacity: 0.9;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const ReplyPreviewContainer = styled(Box)`
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  padding: 8px 12px;
  border-left: 3px solid #1976d2;
  margin-bottom: 8px;
  border-radius: 4px;
  gap: 8px;
`;

const ReplyPreviewContent = styled(Box)`
  flex: 1;
  min-width: 0;
`;

const ReplyPreviewText = styled(Typography)`
  font-size: 0.85rem;
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MessageHeader = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 0.75rem;
  opacity: 0.8;
`;

const MessageText = styled(Typography)`
  word-wrap: break-word;
  white-space: pre-wrap;
`;

const MessageMeta = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 4px;
  font-size: 0.7rem;
  opacity: 0.7;
`;

const MessageActions = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ObservationsContainer = styled(Box)`
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  border-left: 3px solid var(--warning-color);
  background-color: var(--warning-bg);
  color: var(--text-strong);
`;

const ObservationsLabel = styled(Typography)`
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 4px;
  opacity: 0.9;
  color: var(--text-strong);
`;

const ObservationsText = styled(Typography)`
  font-size: 0.8rem;
  white-space: pre-wrap;
  word-break: break-word;
  color: var(--text-strong);
`;

const ObservationsTextarea = styled.textarea`
  width: 100%;
  border: 1px solid var(--surface-border-subtle);
  border-radius: 4px;
  outline: none;
  padding: 8px;
  font-size: 0.85rem;
  font-family: inherit;
  resize: vertical;
  min-height: 70px;
  background: var(--brand-accent);
` as any;

const InputContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background-color: #ffffff;
  border-top: 1px solid #e0e0e0;
`;

const MessageTypeChip = styled(Chip)<{ $messageType: CommunicationType }>`
  height: 20px;
  font-size: 0.65rem;
  background-color: ${props => {
    if (props.$messageType === CommunicationType.Email) return 'var(--brand-secondary)';
    if (props.$messageType === CommunicationType.WhatsApp) return 'var(--success-color)';
    if (props.$messageType === CommunicationType.PhoneCall) return 'var(--info-color-strong)';
    return 'var(--brand-primary)';
  }};
  color: var(--brand-accent);
`;

const TypeSelectorContainer = styled(FormControl)`
  width: fit-content;
  min-width: 140px;
`;

const ComposerInlineActions = styled(Box)`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
`;

const ComposerActionButton = styled(IconButton)`
  padding: var(--spacer-XS);
  border: 1px solid var(--surface-border-subtle);
  border-radius: 8px;
  background: var(--brand-accent);

  &:hover {
    background: var(--bg-light);
  }
`;

const AttachmentList = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
`;

const AttachmentLink = styled.a`
  color: inherit;
  text-decoration: underline;
  word-break: break-all;
` as any;

const getGoogleReviewChannelLabel = (t: TranslateFn, channel: CommunicationType): string => {
  if (channel === CommunicationType.Email) {
    return t(TK.email);
  }

  if (channel === CommunicationType.WhatsApp) {
    return t(TK.whatsApp);
  }

  return t(TK.sms);
};

export const getGoogleReviewIconColor = (googleReviewSummary?: GoogleReviewSummary): string | undefined => {
  if (!googleReviewSummary || googleReviewSummary.state === GoogleReviewState.NotSent) {
    return undefined;
  }

  if (googleReviewSummary.state === GoogleReviewState.Pending) {
    return 'var(--warning-color)';
  }

  return (googleReviewSummary.stars ?? 0) >= 4 ? 'var(--success-color)' : 'var(--info-color)';
};

export const getGoogleReviewIconTooltip = (
  t: TranslateFn,
  googleReviewSummary: GoogleReviewSummary | undefined,
  googleReviewChannelLabel: string,
  requestTooltip: string,
): string => {
  if (!googleReviewSummary || googleReviewSummary.state === GoogleReviewState.NotSent) {
    return requestTooltip;
  }

  const date = moment(googleReviewSummary.sentAt).format('MMM D, HH:mm');

  if (googleReviewSummary.state === GoogleReviewState.Pending) {
    return t(TK.googleReviewIconTooltipPending, date, googleReviewChannelLabel);
  }

  const stars = String(googleReviewSummary.stars ?? 0);
  return (googleReviewSummary.stars ?? 0) >= 4
    ? t(TK.googleReviewIconTooltipSubmittedPositive, date, stars)
    : t(TK.googleReviewIconTooltipSubmittedNegative, date, stars);
};

const SubjectInputContainer = styled(Box)`
  display: flex;
  align-items: center;
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 4px;
  padding: 8.5px 14px;
  width: 100%;
  transition: border-color 0.2s;

  &:hover {
    border-color: rgba(0, 0, 0, 0.87);
  }

  &:focus-within {
    border-color: #1976d2;
    border-width: 2px;
    padding: 7.5px 13px;
  }
`;

const CompanyPrefix = styled.span`
  color: #999;
  font-size: 1rem;
  white-space: nowrap;
  margin-right: 8px;
  user-select: none;
  font-style: italic;
`;

const SubjectInput = styled.input`
  border: none;
  outline: none;
  flex: 1;
  font-size: 1rem;
  font-family: inherit;
  background: transparent;

  &::placeholder {
    color: rgba(0, 0, 0, 0.38);
  }
` as any;

const MessageInputContainer = styled(Box)`
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 4px;
  transition: border-color 0.2s;
  overflow: hidden;

  &:hover {
    border-color: rgba(0, 0, 0, 0.87);
  }

  &:focus-within {
    border-color: #1976d2;
    border-width: 2px;
  }
`;

const MessageTextarea = styled.textarea`
  width: 100%;
  border: none;
  outline: none;
  padding: 14px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;
  background: transparent;

  &::placeholder {
    color: rgba(0, 0, 0, 0.38);
  }
` as any;

const ReadOnlySignature = styled(Box)`
  background-color: #fafafa;
  border-top: 1px solid #e0e0e0;
  padding: 10px 14px;
  color: #999;
  font-size: 0.875rem;
  white-space: pre-wrap;
  font-family: inherit;
  line-height: 1.5;
  font-style: italic;
  user-select: none;
`;

const SignatureTextarea = styled.textarea`
  width: 100%;
  border: none;
  border-top: 1px solid #e0e0e0;
  outline: none;
  padding: 10px 14px;
  font-size: 0.875rem;
  font-family: inherit;
  resize: none;
  min-height: 60px;
  background-color: #fafafa;
  color: #999;
  font-style: italic;

  &::placeholder {
    color: rgba(0, 0, 0, 0.38);
  }
` as any;

const WhatsAppDisclaimer = styled(Typography)`
  color: #777;
  font-size: 0.75rem;
  white-space: nowrap;
  line-height: 1rem;

  @media (max-width: 600px) {
    padding: 4px 0;
    white-space: normal;
  }
`;

const TextareaWrapper = styled(Box)`
  position: relative;
`;

export const CommunicationChat: React.FC<CommunicationChatProps> = ({
  messages,
  sendMessage,
  onToggleRead,
  onUpdateObservations,
  disabled = false,
  isOpen = true,
  onRefresh,
  clientName,
  clientPhoneNumbers,
  mintuPausedUntil,
  onMintuPauseChange,
  onRequestGoogleReview,
  googleReviewSummary,
}) => {
  const dispatch = useDispatch();
  const t = useTranslations();
  const user = useSelector(getMyUser);
  const professional = useSelector(getMyProffesionalData);
  const generalSettings = useSelector(getGeneralSettings);
  const communicationQuotas = useSelector(getCommunicationQuotas);
  const [inputValue, setInputValue] = useState('');
  const [observationsValue, setObservationsValue] = useState('');
  const [isObservationsExpanded, setIsObservationsExpanded] = useState(false);
  const [subject, setSubject] = useState('');
  const [messageType, setMessageType] = useState<CommunicationType>(CommunicationType.Email);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [replyingTo, setReplyingTo] = useState<CommunicationData | null>(null);
  const [editingObservationsId, setEditingObservationsId] = useState<string | null>(null);
  const [editingObservationsValue, setEditingObservationsValue] = useState('');
  const [savingObservations, setSavingObservations] = useState(false);
  const [mintuMenuAnchor, setMintuMenuAnchor] = useState<null | HTMLElement>(null);
  const [confirmGoogleReviewOpen, setConfirmGoogleReviewOpen] = useState(false);
  const [sendingGoogleReview, setSendingGoogleReview] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState<string | undefined>(undefined);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const messagesContainerRef = React.useRef<any>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const messageRefs = React.useRef<Map<string, any>>(new Map());
  const [signature, setSignature] = React.useState<string>(`---\n${professional?.name ?? user?.name ?? ''}\n${generalSettings.companyName ?? 'Appointmint'}`);
  const language = getLanguage();

  const scrollToBottom = React.useCallback((behavior: ScrollBehavior = 'auto') => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior });
      return;
    }

    messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
  }, []);

  // Sort messages by timestamp (oldest first, newest last)
  const sortedMessages = React.useMemo(() => {
    return [...messages].sort((a, b) => {
      return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
    });
  }, [messages]);

  React.useEffect(() => {
    if (!sortedMessages.length || !isOpen) return;
    const handle = window.requestAnimationFrame(() => scrollToBottom('auto'));
    return () => window.cancelAnimationFrame(handle);
  }, [sortedMessages, scrollToBottom, isOpen]);

  React.useEffect(() => {
    if (messageType !== CommunicationType.Email && selectedFiles.length > 0) {
      setSelectedFiles([]);
    }
  }, [messageType, selectedFiles.length]);

  React.useEffect(() => {
    if (messageType === CommunicationType.PhoneCall) {
      setIsObservationsExpanded(true);
    }
  }, [messageType]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setSelectedFiles(prev => [...prev, ...Array.from(files)]);
    // Reset input value so the same file can be selected again
    e.target.value = '';
  };

  const handleRemoveFile = (fileName: string) => {
    setSelectedFiles(prev => prev.filter(f => f.name !== fileName));
  };

  const handleSend = async () => {
    const messageValue = inputValue.trim();
    const observations = observationsValue.trim();
    if (disabled) {
      return;
    }
    if (isWhatsAppReplyWindowClosed) {
      return;
    }
    if (messageType === CommunicationType.PhoneCall && !observations) {
      return;
    }
    if (messageType !== CommunicationType.PhoneCall && !messageValue) {
      return;
    }

    // For email, subject is required only for new messages (not replies)
    if (messageType === CommunicationType.Email && !replyingTo && !subject.trim()) {
      return;
    }

    const brandedMessage = messageType === CommunicationType.PhoneCall
      ? ''
      : withBranding(messageValue, messageType);

    // Only brand subject for new emails, not replies (backend handles reply subjects)
    const brandedSubject = messageType === CommunicationType.Email && !replyingTo
      ? withBrandedSubject(subject.trim())
      : subject.trim();

    try {
      if (await sendMessage(
        brandedMessage,
        messageType,
        brandedSubject || undefined,
        selectedFiles,
        replyingTo?.conversationId,
        observations || undefined
      )) {
        setInputValue('');
        setObservationsValue('');
        setIsObservationsExpanded(false);
        setSubject('');
        setSelectedFiles([]);
        setReplyingTo(null);
      }
    } catch (err) {
      console.error('Failed to send message', err);
    }
  };

  const handleReply = (message: CommunicationData) => {
    if (message.type === CommunicationType.Email) {
      setReplyingTo(message);
      setMessageType(CommunicationType.Email);
      if (message.subject && !subject) {
        const reSubject = message.subject.startsWith('Re: ') ? message.subject : `Re: ${message.subject}`;
        setSubject(reSubject);
      }
    }
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
    setSubject('');
  };

  const handleEditObservations = (message: CommunicationData) => {
    setEditingObservationsId(message.id);
    setEditingObservationsValue(message.observations ?? '');
  };

  const handleCancelEditObservations = () => {
    setEditingObservationsId(null);
    setEditingObservationsValue('');
  };

  const handleSaveObservations = async (message: CommunicationData) => {
    if (!onUpdateObservations || savingObservations) {
      return;
    }

    try {
      setSavingObservations(true);
      const nextValue = editingObservationsValue.trim();
      const success = await onUpdateObservations(message, nextValue || undefined);
      if (success) {
        handleCancelEditObservations();
      }
    } catch (err) {
      console.error('Failed to update observations', err);
    } finally {
      setSavingObservations(false);
    }
  };

  const handleToggleRead = async (message: CommunicationData, currentReadStatus: boolean) => {
    if (!onToggleRead) return;
    try {
      await onToggleRead(message, !currentReadStatus);
    } catch (err) {
      console.error('Failed to toggle read status', err);
    }
  };

  const scrollToMessage = (messageId: string) => {
    const element = messageRefs.current.get(messageId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.style.backgroundColor = '#ffeb3b';
      setTimeout(() => {
        element.style.backgroundColor = '';
      }, 1000);
    }
  };

  const findOriginalMessage = (message: CommunicationData): CommunicationData | undefined => {
    const conversationId = message.conversationId;
    if (!conversationId) return undefined;
    const messageTimestamp = new Date(message.timestamp).getTime();
    return sortedMessages.reduce<CommunicationData | undefined>((latest, candidate) => {
      if (candidate.conversationId !== conversationId || candidate.id === message.id) {
        return latest;
      }

      const candidateTimestamp = new Date(candidate.timestamp).getTime();
      if (candidateTimestamp >= messageTimestamp) {
        return latest;
      }

      if (!latest) {
        return candidate;
      }

      const latestTimestamp = new Date(latest.timestamp).getTime();
      return candidateTimestamp > latestTimestamp ? candidate : latest;
    }, undefined);
  };

  const formatTimestamp = (timestamp: string) => {
    return moment(timestamp).format('MMM D, HH:mm');
  };

  const getMessageIcon = (type: CommunicationType) => {
    if (type === CommunicationType.Email) return <EmailIcon fontSize="small" />;
    if (type === CommunicationType.WhatsApp) return <WhatsAppIcon fontSize="small" />;
    if (type === CommunicationType.PhoneCall) return <PhoneIcon fontSize="small" />;
    return <SmsIcon fontSize="small" />;
  };

  const getStatusLabel = (status: CommunicationStatus | undefined) => {
    switch (status) {
      case CommunicationStatus.Pending: return t(TK.pending);
      case CommunicationStatus.Failed: return t(TK.failed);
      default: return t(TK.sent);
    }
  };

  const companyName = React.useMemo(
    () => (generalSettings.companyName ?? '').trim() || t(TK.company),
    [generalSettings.companyName, t]
  );

  const lastWhatsAppReply = React.useMemo(() => {
    return messages
      .filter(message => message.type === CommunicationType.WhatsApp && message.direction === CommunicationDirection.Received)
      .reduce<CommunicationData | null>((latest, message) => {
        if (!latest) return message;
        return new Date(message.timestamp).getTime() > new Date(latest.timestamp).getTime() ? message : latest;
      }, null);
  }, [messages]);

  const hasRecentWhatsAppReply = React.useMemo(() => {
    if (!lastWhatsAppReply) return false;
    const hoursSinceReply = moment().diff(moment(lastWhatsAppReply.timestamp), 'hours', true);
    return hoursSinceReply <= 24;
  }, [lastWhatsAppReply]);

  const isWhatsAppReplyWindowClosed = messageType === CommunicationType.WhatsApp && !hasRecentWhatsAppReply;

  const withBranding = React.useCallback((message: string, type: CommunicationType) => {
    const trimmed = message.trim();
    if (!trimmed) {
      return '';
    }

    // Prevent duplicate branding by checking if signature already exists
    if (trimmed.includes(`\n---\n`)) {
      return trimmed;
    }
    return `${trimmed}\n\n${signature}`;
  }, [signature]);

  const withBrandedSubject = React.useCallback((value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      return companyName;
    }
    return `${companyName} / ${trimmed}`;
  }, [companyName]);

  const isMintuPaused = React.useMemo(() => {
    if (!mintuPausedUntil) return false;
    return new Date(mintuPausedUntil).getTime() > Date.now();
  }, [mintuPausedUntil]);
  const showMintuToggle = messageType === CommunicationType.WhatsApp && !!onMintuPauseChange;

  React.useEffect(() => {
    if (!onRequestGoogleReview || communicationQuotas) {
      return;
    }

    dispatch(loadCommunicationQuotasIfNeeded());
  }, [communicationQuotas, dispatch, onRequestGoogleReview]);

  const mintuPausedUntilFormatted = React.useMemo(() => {
    if (!mintuPausedUntil || !isMintuPaused) return '';
    return moment(mintuPausedUntil).format('HH:mm');
  }, [mintuPausedUntil, isMintuPaused]);

  React.useEffect(() => {
    if (!showMintuToggle) {
      setMintuMenuAnchor(null);
    }
  }, [showMintuToggle]);

  const handleMintuPauseOption = async (pauseHours?: number) => {
    setMintuMenuAnchor(null);
    if (onMintuPauseChange) {
      await onMintuPauseChange(pauseHours);
    }
  };

  const hasClientPhoneNumber = React.useMemo(
    () => !!clientPhoneNumbers?.some(phoneNumber => !!phoneNumber?.trim()),
    [clientPhoneNumbers]
  );

  const hasWhatsAppQuotaAvailable = React.useMemo(() => {
    if (!communicationQuotas) {
      return false;
    }

    return communicationQuotas.whatsAppUsed < communicationQuotas.whatsAppAllowed;
  }, [communicationQuotas]);

  const branches = React.useMemo(() => {
    const b = generalSettings.branches ?? {};
    return Object.entries(b).map(([id, s]) => ({ id, displayName: s.displayName, googleReviewUrl: s.googleReviewUrl }));
  }, [generalSettings.branches]);
  const isMultiBranch = branches.length > 1;
  const hasGoogleReviewUrl = React.useMemo(() => {
    if (generalSettings.googleReviewUrl) {
      return true;
    }

    return branches.some(branch => !!branch.googleReviewUrl);
  }, [branches, generalSettings.googleReviewUrl]);
  const effectiveBranchId = isMultiBranch ? selectedBranchId : branches[0]?.id;

  const isGoogleReviewAvailable = hasGoogleReviewUrl && messageType !== CommunicationType.PhoneCall;
  const showGoogleReviewButton = !!onRequestGoogleReview && messageType !== CommunicationType.PhoneCall;
  const googleReviewNeedsPhone = messageType === CommunicationType.WhatsApp || messageType === CommunicationType.SMS;
  const googleReviewNeedsWhatsAppQuota = messageType === CommunicationType.WhatsApp;
  const googleReviewRequestTooltip = !isGoogleReviewAvailable
    ? t(TK.googleReviewUrlNotConfiguredTooltip)
    : googleReviewNeedsPhone && !hasClientPhoneNumber && googleReviewNeedsWhatsAppQuota && !hasWhatsAppQuotaAvailable
    ? t(TK.googleReviewWhatsAppRequirementsMissingTooltip)
    : googleReviewNeedsPhone && !hasClientPhoneNumber
    ? t(TK.googleReviewWhatsAppPhoneRequiredTooltip)
    : googleReviewNeedsWhatsAppQuota && !hasWhatsAppQuotaAvailable
    ? t(TK.googleReviewWhatsAppQuotaExhaustedTooltip)
    : t(TK.googleReviewIconTooltipNotSent);
  const googleReviewChannelLabel = getGoogleReviewChannelLabel(t, googleReviewSummary?.channel ?? messageType);
  const googleReviewSummaryTooltip = getGoogleReviewIconTooltip(t, googleReviewSummary, googleReviewChannelLabel, googleReviewRequestTooltip);
  const reviewIconColor = getGoogleReviewIconColor(googleReviewSummary);
  const googleReviewTooltip = googleReviewSummaryTooltip;

  const handleGoogleReviewConfirm = async () => {
    if (!onRequestGoogleReview || sendingGoogleReview) return;
    try {
      setSendingGoogleReview(true);
      await onRequestGoogleReview(messageType, effectiveBranchId);
      setConfirmGoogleReviewOpen(false);
    } finally {
      setSendingGoogleReview(false);
    }
  };

  const canSend = React.useMemo(() => {
    const messageValue = inputValue.trim();
    const observations = observationsValue.trim();
    const hasRequiredContent = messageType === CommunicationType.PhoneCall
      ? !!observations
      : !!messageValue;

    return hasRequiredContent
      && !disabled
      && !isWhatsAppReplyWindowClosed
      && !(messageType === CommunicationType.Email && !replyingTo && !subject.trim());
  }, [disabled, inputValue, isWhatsAppReplyWindowClosed, messageType, observationsValue, replyingTo, subject]);

  const translatedPhoneCallLabel = t(TK.phoneCall);
  const phoneCallLabel = translatedPhoneCallLabel !== TK.phoneCall
    ? translatedPhoneCallLabel
    : language === 'pt'
    ? 'Chamada telefónica'
    : language === 'fr'
    ? 'Appel téléphonique'
    : 'Phone call';

  const isPhoneCall = messageType === CommunicationType.PhoneCall;

  const internalObservationsLabel = language === 'pt'
    ? 'Observações internas'
    : language === 'fr'
    ? 'Observations internes'
    : 'Internal observations';

  const addInternalObservationsLabel = language === 'pt'
    ? 'Adicionar observações internas...'
    : language === 'fr'
    ? 'Ajouter des observations internes...'
    : 'Add internal observations...';

  const phoneCallDisclaimerLabel = language === 'pt'
    ? 'Apenas para registo interno de chamadas e notas'
    : language === 'fr'
    ? 'Uniquement pour le suivi interne des appels et notes'
    : 'For internal call tracking and notes only';

  return (
    <ChatContainer elevation={3}>
      <MessagesContainer ref={messagesContainerRef}>
        {sortedMessages.map((message) => {
          const isSent = message.direction === CommunicationDirection.Sent;
          const fromMe = message.username === user?.username;
          const originalMessage = findOriginalMessage(message);

          return (
            <MessageBubble
              key={message.id}
              $isSent={isSent}
              ref={(el) => {
                if (el) {
                  messageRefs.current.set(message.id, el);
                }
              }}
            >
              <MessageHeader>
                {getMessageIcon(message.type)}
                <span>{isSent ? (fromMe ? t(TK.you) : message.name || message.username || t(TK.automatic)) : (message.professionalName || message.clientName || t(TK.unknown))}</span>
                <MessageTypeChip
                  $messageType={message.type}
                  label={
                    message.type === CommunicationType.Email
                      ? t(TK.email)
                      : message.type === CommunicationType.WhatsApp
                      ? 'WhatsApp'
                      : message.type === CommunicationType.PhoneCall
                      ? phoneCallLabel
                      : t(TK.sms)
                  }
                  size="small"
                />
              </MessageHeader>

              {originalMessage && (
                <QuotedMessage
                  $isSent={isSent}
                  onClick={() => scrollToMessage(originalMessage.id)}
                >
                  <Typography variant="caption" style={{ fontWeight: 600, opacity: 0.8, marginBottom: 4 }}>
                    {originalMessage.direction === CommunicationDirection.Sent
                      ? t(TK.you)
                      : (originalMessage.professionalName || originalMessage.clientName || t(TK.unknown))}
                  </Typography>
                  <QuotedMessageText variant="body2">
                    {originalMessage.text}
                  </QuotedMessageText>
                </QuotedMessage>
              )}

              {message.subject && (
                <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: 4 }}>
                  {message.subject}
                </Typography>
              )}

              <MessageText variant="body2">
                {message.text}
              </MessageText>

              {message.attachments && message.attachments.length > 0 && (
                <AttachmentList>
                  {message.attachments.map(att => (
                    <AttachmentLink key={att.id} href={att.url} target="_blank" rel="noopener noreferrer">
                      <AttachFileIcon fontSize="medium" style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      {att.fileName}
                    </AttachmentLink>
                  ))}
                </AttachmentList>
              )}

              {(editingObservationsId === message.id || !!message.observations) && (
                <ObservationsContainer>
                  <ObservationsLabel variant="caption">{internalObservationsLabel}</ObservationsLabel>
                  {editingObservationsId === message.id ? (
                    <Box>
                      <ObservationsTextarea
                        value={editingObservationsValue}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditingObservationsValue(e.target.value)}
                        disabled={disabled || savingObservations}
                      />
                      <Box display="flex" justifyContent="flex-end" style={{ marginTop: 6, gap: 4 }}>
                        <IconButton
                          size="small"
                          onClick={handleCancelEditObservations}
                          disabled={savingObservations}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleSaveObservations(message)}
                          disabled={savingObservations}
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  ) : (
                    <ObservationsText variant="body2">
                      {message.observations}
                    </ObservationsText>
                  )}
                </ObservationsContainer>
              )}

              <MessageMeta>
                <Tooltip
                  title={message.reads && message.reads.length > 0
                    ? (
                      <div>
                        <div>{t(TK.receivedAt)} {moment(message.timestamp).format('MMM D, HH:mm')}</div>
                        {message.reads.map((r, i) => (
                          <div key={i}>{t(TK.readBy)} {r.username} · {moment(r.readAt).format('MMM D, HH:mm')}</div>
                        ))}
                      </div>
                    )
                    : ''
                  }
                >
                  <span style={message.reads && message.reads.length > 0 ? { cursor: 'help' } : undefined}>
                    {formatTimestamp(message.statusUpdateTimestamp ?? message.timestamp)}
                    {message.direction === CommunicationDirection.Sent && <>
                      &nbsp;
                      {message.status === CommunicationStatus.Failed && (
                        <ErrorOutlineIcon style={{ fontSize: '0.85rem', verticalAlign: 'middle', marginRight: 2 }} />
                      )}
                      (<span style={{ color: message.status === CommunicationStatus.Pending ? 'var(--warning-color)' : undefined }}>
                        {getStatusLabel(message.status)}
                      </span>)
                    </>}
                  </span>
                </Tooltip>
                <MessageActions>
                  {message.type === CommunicationType.Email && (
                    <Tooltip title={t(TK.reply)}>
                      <IconButton
                        color="inherit"
                        onClick={() => handleReply(message)}
                        disabled={disabled}
                        style={{ padding: 'var(--spacer-XXS)', marginLeft: 'var(--spacer-XXS)' }}
                      >
                        <ReplyIcon style={{ fontSize: '1.1rem' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {onUpdateObservations && (
                    <Tooltip title={t(TK.edit)}>
                      <IconButton
                        color="inherit"
                        onClick={() => handleEditObservations(message)}
                        disabled={disabled || savingObservations}
                        style={{ padding: 'var(--spacer-XXS)', marginLeft: 'var(--spacer-XXS)' }}
                      >
                        <EditIcon style={{ fontSize: '1.1rem' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {onToggleRead && message.direction === CommunicationDirection.Received && (
                    <Tooltip title={message.isRead ? t(TK.markAsUnread) : t(TK.markAsRead)}>
                      <IconButton
                        color="inherit"
                        onClick={() => handleToggleRead(message, message.isRead)}
                        disabled={disabled}
                        style={{ padding: 'var(--spacer-XXS)', marginLeft: 'var(--spacer-XXS)' }}
                      >
                        {message.isRead ? (
                          <DraftsIcon style={{ fontSize: '1.2rem' }} />
                        ) : (
                          <MailOutlineIcon style={{ fontSize: '1.2rem' }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                </MessageActions>
              </MessageMeta>
            </MessageBubble>
          );
        })}
        <div ref={messagesEndRef} />
      </MessagesContainer>

      {!!handleSend &&
        <InputContainer>
          {replyingTo && (
            <ReplyPreviewContainer>
              <ReplyIcon fontSize="small" style={{ color: '#1976d2' }} />
              <ReplyPreviewContent>
                <Typography variant="caption" style={{ fontWeight: 600, color: '#1976d2' }}>
                  {t(TK.replyingTo)} {replyingTo.direction === CommunicationDirection.Sent
                    ? t(TK.you)
                    : (replyingTo.professionalName || replyingTo.clientName || t(TK.unknown))}
                </Typography>
                <ReplyPreviewText variant="body2">
                  {replyingTo.text}
                </ReplyPreviewText>
              </ReplyPreviewContent>
              <IconButton size="small" onClick={handleCancelReply}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </ReplyPreviewContainer>
          )}

          <Box display="flex" alignItems="flex-start" justifyContent="space-between" style={{ gap: 8 }}>
            <Box display="flex" flexDirection="column" style={{ gap: 'var(--spacer-XXS)', flex: 1, minWidth: 0 }}>
              <Box display="flex" alignItems="center" style={{ gap: 10, flexWrap: 'wrap' }}>
                <TypeSelectorContainer variant="standard" size="small">
                  <Select
                    value={messageType}
                    onChange={(e: React.ChangeEvent<{ value: unknown }>) => setMessageType(e.target.value as CommunicationType)}
                    disabled={disabled || !!replyingTo}
                    style={{height: '1rem', fontSize: '0.8rem'}}
                    disableUnderline
                  >
                    <MenuItem value={CommunicationType.Email}>
                      <Box display="flex" alignItems="center">
                        <EmailIcon style={{ marginRight: 8 }} />
                        {t(TK.email)}
                      </Box>
                    </MenuItem>
                    <MenuItem value={CommunicationType.SMS}>
                      <Box display="flex" alignItems="center">
                        <SmsIcon style={{ marginRight: 8 }} />
                        {t(TK.sms)}
                      </Box>
                    </MenuItem>
                    <MenuItem value={CommunicationType.WhatsApp}>
                      <Box display="flex" alignItems="center">
                        <WhatsAppIcon style={{ marginRight: 8 }} />
                        WhatsApp
                      </Box>
                    </MenuItem>
                    <MenuItem value={CommunicationType.PhoneCall}>
                      <Box display="flex" alignItems="center">
                        <PhoneIcon style={{ marginRight: 8 }} />
                        {phoneCallLabel}
                      </Box>
                    </MenuItem>
                  </Select>
                </TypeSelectorContainer>
                <ComposerInlineActions>
                  {onRefresh && (
                    <Tooltip title={t(TK.refresh)}>
                      <span>
                        <ComposerActionButton
                          size="small"
                          onClick={onRefresh}
                          disabled={disabled}
                          color="default"
                        >
                          <RefreshIcon fontSize="small" />
                        </ComposerActionButton>
                      </span>
                    </Tooltip>
                  )}

                  {messageType === CommunicationType.Email && (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                        disabled={disabled}
                      />
                      <Tooltip title={t(TK.attachFiles)}>
                        <span>
                          <ComposerActionButton
                            size="small"
                            color="default"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={disabled}
                          >
                            <AttachFileIcon fontSize="small" />
                          </ComposerActionButton>
                        </span>
                      </Tooltip>
                    </>
                  )}

                  {showGoogleReviewButton && (
                    <Tooltip title={googleReviewTooltip}>
                      <span>
                        <ComposerActionButton
                          data-testid="google-review-action-button"
                          size="small"
                          color="default"
                          onClick={() => { setSelectedBranchId(undefined); setConfirmGoogleReviewOpen(true); }}
                          disabled={!isGoogleReviewAvailable || (googleReviewNeedsPhone && !hasClientPhoneNumber) || (googleReviewNeedsWhatsAppQuota && !hasWhatsAppQuotaAvailable) || disabled}
                          style={{ color: reviewIconColor }}
                        >
                          <GoogleIcon width={20} height={20} />
                        </ComposerActionButton>
                      </span>
                    </Tooltip>
                  )}
                </ComposerInlineActions>
                {showMintuToggle && (
                  <>
                    <Chip
                      size="small"
                      icon={isMintuPaused ? <PauseCircleOutlineIcon fontSize="small" /> : <PlayCircleOutlineIcon fontSize="small" />}
                      label={isMintuPaused ? `${t(TK.mintuPausedUntil)} ${mintuPausedUntilFormatted}` : t(TK.mintuActive)}
                      onClick={(e) => setMintuMenuAnchor(e.currentTarget)}
                      style={{
                        backgroundColor: isMintuPaused ? 'var(--warning-bg)' : 'var(--success-bg)',
                        borderColor: isMintuPaused ? 'var(--warning-border)' : 'var(--success-border)',
                        borderWidth: 'var(--border-thin)',
                        borderStyle: 'solid',
                        cursor: 'pointer',
                      }}
                    />
                    <Menu
                      anchorEl={mintuMenuAnchor}
                      open={Boolean(mintuMenuAnchor)}
                      onClose={() => setMintuMenuAnchor(null)}
                    >
                      {isMintuPaused && (
                        <MenuItem onClick={() => handleMintuPauseOption(undefined)}>
                          <PlayCircleOutlineIcon fontSize="small" style={{ marginRight: 'var(--spacer-XS)' }} />
                          {t(TK.mintuResume)}
                        </MenuItem>
                      )}
                      <MenuItem disabled style={{ opacity: 0.6, fontSize: '0.85rem' }}>
                        {t(TK.mintuPauseFor)}
                      </MenuItem>
                      <MenuItem onClick={() => handleMintuPauseOption(1)}>{t(TK.mintuPause1Hour)}</MenuItem>
                      <MenuItem onClick={() => handleMintuPauseOption(2)}>{t(TK.mintuPause2Hours)}</MenuItem>
                      <MenuItem onClick={() => handleMintuPauseOption(4)}>{t(TK.mintuPause4Hours)}</MenuItem>
                      <MenuItem onClick={() => handleMintuPauseOption(8)}>{t(TK.mintuPause8Hours)}</MenuItem>
                      <MenuItem onClick={() => handleMintuPauseOption(24)}>{t(TK.mintuPause24Hours)}</MenuItem>
                    </Menu>
                  </>
                )}
              </Box>
              {isWhatsAppReplyWindowClosed && (
                <WhatsAppDisclaimer variant="caption">
                  * {t(TK.whatsappTemplateRequiredDisclaimer)}
                </WhatsAppDisclaimer>
              )}
              {isPhoneCall && (
                <WhatsAppDisclaimer variant="caption">
                  * {phoneCallDisclaimerLabel}
                </WhatsAppDisclaimer>
              )}
            </Box>
          </Box>

          {messageType === CommunicationType.Email && !replyingTo && (
            <SubjectInputContainer>
              <CompanyPrefix>{companyName} /</CompanyPrefix>
              <SubjectInput
                placeholder={t(TK.subject)}
                value={subject}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSubject(e.target.value)}
                disabled={disabled}
              />
            </SubjectInputContainer>
          )}
          {messageType === CommunicationType.Email && selectedFiles.length > 0 && (
            <Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
              {selectedFiles.map(file => {
                const sizeKB = (file.size / 1024).toFixed(1);
                const label = `${file.name} (${sizeKB} KB)`;
                return (
                  <Chip
                    key={file.name}
                    size="small"
                    label={label}
                    onDelete={() => handleRemoveFile(file.name)}
                  />
                );
              })}
            </Box>
          )}

          <Box display="flex" alignItems="flex-start" style={{ gap: 8 }}>
            <Box style={{ flex: 1 }}>
              {messageType !== CommunicationType.PhoneCall && (
                <MessageInputContainer>
                  <TextareaWrapper>
                    <MessageTextarea
                      placeholder={t(
                        messageType === CommunicationType.Email
                          ? TK.typeYourEmailMessage
                          : messageType === CommunicationType.WhatsApp
                          ? TK.typeYourWhatsAppMessage
                          : TK.typeYourSmsMessage
                      )}
                      value={inputValue}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                        setInputValue(e.target.value);
                      }}
                      disabled={disabled || isWhatsAppReplyWindowClosed}
                    />
                  </TextareaWrapper>

                  {!isWhatsAppReplyWindowClosed && (
                    <SignatureTextarea
                      value={signature}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setSignature(e.target.value)
                      }
                      disabled={disabled}
                    />
                  )}
                </MessageInputContainer>
              )}
              <Box style={{ marginTop: 8 }}>
                {(isPhoneCall || isObservationsExpanded || observationsValue.trim().length > 0) ? (
                  <>
                    <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 4 }}>
                      <Typography variant="caption">
                        {internalObservationsLabel}
                      </Typography>
                      {!isPhoneCall && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setIsObservationsExpanded(false);
                            setObservationsValue('');
                          }}
                          disabled={disabled}
                          style={{ padding: 2 }}
                        >
                          <CloseIcon style={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      )}
                    </Box>
                    <ObservationsTextarea
                      value={observationsValue}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setObservationsValue(e.target.value)}
                      placeholder={`${internalObservationsLabel}...`}
                      disabled={disabled}
                      style={{ minHeight: 56 }}
                    />
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setIsObservationsExpanded(true)}
                    disabled={disabled}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      padding: 0,
                      fontSize: '0.85rem',
                      color: '#0366d6',
                      cursor: disabled ? 'default' : 'pointer',
                      fontFamily: 'inherit',
                      opacity: disabled ? 0.6 : 1,
                    }}
                  >
                    {addInternalObservationsLabel}
                  </button>
                )}
              </Box>
            </Box>

            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="flex-end" style={{ gap: 4 }}>
              <Tooltip title={isPhoneCall ? t(TK.register) : ''}>
                <span>
                  <IconButton
                    size="medium"
                    color="primary"
                    onClick={handleSend}
                    disabled={!canSend}
                  >
                    {isPhoneCall ? <CheckIcon fontSize="small" /> : <SendIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </InputContainer>
      }

      <Dialog
        open={confirmGoogleReviewOpen}
        onClose={() => { if (!sendingGoogleReview) setConfirmGoogleReviewOpen(false); }}
      >
        <DialogTitle>{t(TK.requestGoogleReviewConfirmTitle)}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(TK.requestGoogleReviewConfirmBody, googleReviewChannelLabel)}
          </DialogContentText>
          {isMultiBranch && (
            <FormControl fullWidth style={{ marginTop: 16 }}>
              <Select
                value={selectedBranchId ?? ''}
                onChange={(e: React.ChangeEvent<{ value: unknown }>) => setSelectedBranchId(e.target.value as string || undefined)}
                displayEmpty
                disabled={sendingGoogleReview}
              >
                <MenuItem value="">{t(TK.googleReviewSelectBranchPlaceholder)}</MenuItem>
                {branches.map(b => (
                  <MenuItem key={b.id} value={b.id} disabled={!b.googleReviewUrl}>{b.displayName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmGoogleReviewOpen(false)}
            disabled={sendingGoogleReview}
          >
            {t(TK.cancel)}
          </Button>
          <Button
            onClick={handleGoogleReviewConfirm}
            color="primary"
            disabled={sendingGoogleReview || (isMultiBranch && !selectedBranchId)}
          >
            {t(TK.send)}
          </Button>
        </DialogActions>
      </Dialog>
    </ChatContainer>
  );
};
